import { TableFooter } from '@fukuri-admins/features/Table/TableFooter';
import { FileDownloadIcon } from '@fukuri-admins/ui/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { downloadFile } from '@fukuri-admins/utils/common';
import { CircularProgress } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getUserDailyFirstAccessLogs,
  getUserDailyFirstAccessLogsCSV,
} from '../../../api/user_daily_first_access_logs';
import { useAuth } from '../../../components/auth/AuthProvider';
import { ContentsHeader } from '../../../components/common/ContentsHeader';
import { UserDailyFirstAccessLogTableContent } from '../../../components/UserDailyFirstAccessLog/UserDailyFirstAccessLogTableContent';
import { UserDailyFirstAccessLog } from '../../../types/resources/UserDailyFirstAccessLogType';
import { ActionTableColumnsType } from '../../../types/table/ActionTableColumnsType';
import {
  ActionTableSortParamsType,
  defaultActionTableSortParams,
} from '../../../types/table/ActionTableSortParamsType';
import { handleSorting } from '../../../utils/sortings';
import styles from './styles.module.scss';

export const IndexUserDailyFirstAccessLog = () => {
  const { signOut } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filterParams] = useSearchParams();
  const [totalPage, setTotalPage] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [filteredUserDailyFirstAccessLogs, setFilteredUserDailyFirstAccessLogs] = useState<
    UserDailyFirstAccessLog[]
  >([]);
  const [sortParams, setSortParams] = useState<ActionTableSortParamsType>({
    ...defaultActionTableSortParams,
    column: 'no',
  });

  const [outputCsvFetchDone, setOutputCsvFetchDone] = useState(false);
  const outputCsvFetchDoneRef = useRef<{ link: HTMLAnchorElement } | null>(null);
  const { showToast } = useToast();

  const adminTableColumns: ActionTableColumnsType[] = [
    { label: '会社名', key: 'client_name', sortable: true, filterable: true },
    { label: '名前', key: 'full_name', sortable: true, filterable: true },
    { label: 'メール', key: 'email', sortable: true, filterable: true },
    {
      label: 'アクセス日時',
      key: 'accessed_at',
      sortable: true,
      filterable: true,
    },
  ];

  const fetchAdminUsersData = async (page?: number) => {
    try {
      const pageNumber = page ? page : filterParams.get('page');
      const { data, headers } = await getUserDailyFirstAccessLogs(signOut, {
        ...(pageNumber && { page: pageNumber.toString() }),
      });
      setTotalPage(Number(headers['total-pages']));
      setTotalCount(Number(headers['total-count']));
      const processedUserDailyFirstAccessLogs = data.user_daily_first_access_logs.map(
        (userDailyFirstAccessLog: UserDailyFirstAccessLog) => ({
          id: userDailyFirstAccessLog.id,
          client_name: userDailyFirstAccessLog.user?.client?.name || '-',

          full_name: userDailyFirstAccessLog.user?.full_name || '-',
          email: userDailyFirstAccessLog.user?.email || '-',
          accessed_at: userDailyFirstAccessLog.accessed_at,
        })
      );

      setFilteredUserDailyFirstAccessLogs(processedUserDailyFirstAccessLogs);
    } catch {
      showToast({ message: 'データの取得に失敗しました。', status: 'danger' });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    setIsLoading(true);
    fetchAdminUsersData(page);
  };

  useEffect(() => {
    fetchAdminUsersData();
  }, []);

  useEffect(() => {
    if (outputCsvFetchDone) {
      outputCsvFetchDoneRef.current?.link.click();
      setOutputCsvFetchDone(false);
    }
  }, [outputCsvFetchDone]);

  const handleOutputCsv = async () => {
    try {
      const { data } = await getUserDailyFirstAccessLogsCSV(signOut);
      downloadFile(data, 'user_daily_first_access_log.csv');
    } catch {
      showToast({ message: 'CSVのダウンロードに失敗しました。', status: 'danger' });
    }
  };

  return (
    <div className={styles.Root} data-testid='partner-admin-page'>
      <ContentsHeader
        title='ログイン履歴'
        actions={
          <div className={styles.menuContainer}>
            <div className={styles.outputCsv} onClick={handleOutputCsv}>
              <FileDownloadIcon />
              CSVダウンロード
            </div>
          </div>
        }
      />
      <div className={styles.tableContainer}>
        <div className={styles.Root__Table}>
          {/* TODO: 検索機能を追加する */}
          {/* <div className={styles.searchContainer}>
              <TableSearch onSearch={onSearch} onClear={onClear}>
              <ServiceTableFilter key={filterKey} />
              </TableSearch>
              </div> */}
          {isLoading ? (
            <div className={styles.loading}>
              <CircularProgress size='1rem' color='inherit' />
            </div>
          ) : (
            <>
              <UserDailyFirstAccessLogTableContent
                rows={filteredUserDailyFirstAccessLogs}
                columns={adminTableColumns}
                sortParams={sortParams}
                headerSort={column =>
                  handleSorting(
                    column,
                    sortParams,
                    filteredUserDailyFirstAccessLogs,
                    setSortParams,
                    setFilteredUserDailyFirstAccessLogs
                  )
                }
              />
              <TableFooter
                onPageChange={handlePageChange}
                totalCount={totalCount}
                totalPage={totalPage}
                itemCount={filteredUserDailyFirstAccessLogs.length}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
